import useSWR from "swr";
import { AxiosInstance } from "axios";
import { useMemo } from "react";
import { useCreateStripeAdminAxiosInstance } from "../../Auth/useCreateStripeAdminAxiosInstance.ts";
import { useCreateHeyLightAdminAxiosInstance } from "../../Auth/useCreateHeyLightAdminAxiosInstance.ts";
import { useCreateProductServiceAdminAxiosInstance } from "../../Auth/useCreateProductServiceAdminAxiosInstance.ts";
import { SaleRecord, SalesGateway } from "./SaleRecord.ts";
import { SalesPeriod } from "./salesPeriod.ts";

const SALE_RECORDS_PATH = "/admin/sales-statistics";

const NO_RECORDS: SaleRecord[] = [];

function useSaleRecordsOf({
  gateway,
  range,
  createAxiosInstance,
}: {
  gateway: SalesGateway;
  range: SalesPeriod;
  createAxiosInstance: () => AxiosInstance;
}) {
  const from = range.from.toISOString();
  const to = range.to.toISOString();
  const { data, error, isLoading } = useSWR(
    [SALE_RECORDS_PATH, gateway, from, to],
    async () => {
      const { data } = await createAxiosInstance().get<{
        records: SaleRecord[];
      }>(SALE_RECORDS_PATH, { params: { from, to } });
      return data.records;
    },
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  return {
    records: data ?? NO_RECORDS,
    hasFailed: !!error,
    isLoading: isLoading && data === undefined,
  };
}

export function useSaleRecords(range: SalesPeriod) {
  const stripe = useSaleRecordsOf({
    gateway: SalesGateway.Stripe,
    range,
    createAxiosInstance:
      useCreateStripeAdminAxiosInstance().createAxiosInstance,
  });
  const heyLight = useSaleRecordsOf({
    gateway: SalesGateway.HeyLight,
    range,
    createAxiosInstance:
      useCreateHeyLightAdminAxiosInstance().createAxiosInstance,
  });
  const bonifico = useSaleRecordsOf({
    gateway: SalesGateway.Bonifico,
    range,
    createAxiosInstance:
      useCreateProductServiceAdminAxiosInstance().createAxiosInstance,
  });

  const records = useMemo(
    () => [...stripe.records, ...heyLight.records, ...bonifico.records],
    [stripe.records, heyLight.records, bonifico.records],
  );
  const failedGateways = useMemo(
    () =>
      (
        [
          [SalesGateway.Stripe, stripe.hasFailed],
          [SalesGateway.HeyLight, heyLight.hasFailed],
          [SalesGateway.Bonifico, bonifico.hasFailed],
        ] as const
      )
        .filter(([, hasFailed]) => hasFailed)
        .map(([gateway]) => gateway),
    [stripe.hasFailed, heyLight.hasFailed, bonifico.hasFailed],
  );

  return {
    records,
    failedGateways,
    isLoading: stripe.isLoading || heyLight.isLoading || bonifico.isLoading,
  };
}
